/**
 * 파일명 : evl0107m.tsx
 * 설명 : 프로젝트평가(PM) 화면 - 모바일
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2026-01-14     조동현    최초작성
 * 1.1       2026-01-22     조동현    모바일 버전 생성
 ***********************************************/

/************************************************
 * Import 영역
 ************************************************/

import React, { act, useMemo, useRef } from "react";
import {
  Form,
  Input,
  Select,
  Typography,
  Card,
  Button,
  Tooltip,
  Alert,
  message,
  Modal,
  Space,
  Divider,
  Table,
  Spin,
} from "antd";
import { SaveOutlined, SendOutlined } from "@ant-design/icons";
import { isMobile } from "react-device-detect";
import * as common from "libs/common";
import { callAsyncSvc } from "../../../libs/CommonProxy";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { EvaluationItem } from "../../../redux/models/evl/evl0107m_model";
import { Evl0107mAction } from "../../../redux/slice/evl/evl0107m_slice";

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Evl0107m() {
  // Ant Design Form 인스턴스
  const [form] = Form.useForm();

  // 화면 너비 상태
  const [screenWidth, setScreenWidth] = React.useState<number>(
    window.innerWidth,
  );

  // 로딩 상태
  const [loading, setLoading] = React.useState<boolean>(true);
  const [isWithinDeadline, setIsWithinDeadline] =
    React.useState<boolean>(false);
  const [isPmMatched, setIsPmMatched] = React.useState<boolean>(false);

  // Redux에서 evl0101m에서 전달한 props 받기
  const activeProps = useAppSelector((state) => state.Frame.activeProps);

  // Redux 상태 및 디스패치
  const dispatch = useAppDispatch();
  const { pmScores, projectInfo, evaluationData, isSubmitted, pmSubYn } =
    useAppSelector((state) => state.Evl0107m);
  const confirmModalRefs = useRef<any[]>([]);
  const evaluationTableRef = useRef<HTMLDivElement | null>(null);
  const commentsRef = useRef<HTMLDivElement | null>(null);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  const scrollAndError = (
    ref: React.RefObject<HTMLElement>,
    errorMsg: string,
  ) => {
    message.error(errorMsg);
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
      }
    }, 300);
  };

  const scrollToRowSelector = (selector: string) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  /**
   * dtlTypCd별 그룹 순서
   */
  const groupOrder = useMemo(() => {
    const groups: Record<string, any[]> = {};
    evaluationData.forEach((item) => {
      if (!groups[item.dtlTypCd]) groups[item.dtlTypCd] = [];
      groups[item.dtlTypCd].push(item);
    });
    const order = Array.from(
      new Set(evaluationData.map((item) => item.dtlTypCd)),
    );
    const main = order.filter((dtl) => dtl !== "11");
    const other = order.filter((dtl) => dtl === "11");
    return [...main, ...other];
  }, [evaluationData]);

  /**
   * dtlTypCd별 maxScore 계산
   */
  const maxScores = useMemo(() => {
    const scores: Record<string, number> = {};
    evaluationData
      .filter(
        (item) => item.no !== "" && item.item !== "계" && item.item !== "총계",
      )
      .forEach((item) => {
        if (!scores[item.dtlTypCd]) scores[item.dtlTypCd] = 0;
        scores[item.dtlTypCd] += item.score;
      });
    return scores;
  }, [evaluationData]);

  /**
   * 화면 초기화 - 프로젝트 정보 조회
   * 컴포넌트 마운트 시 Redux에서 받은 props를 이용해 프로젝트 정보를 조회
   */
  React.useEffect(() => {
    if (activeProps && activeProps.evlId && activeProps.empNo) {
      // PM 권한 확인
      const pjPmEmpNo = (activeProps.pjPmEmpNo || "").toString().trim();
      const currentEmpNo = (common.getEmpNo() || "").toString().trim();
      setIsPmMatched(pjPmEmpNo !== "" && pjPmEmpNo === currentEmpNo);

      loadProjectInfo();
    } else if (
      activeProps === null ||
      activeProps === undefined ||
      !activeProps.evlId
    ) {
      // loading을 true로 유지하여 background에 로딩 표시
      setLoading(true);
      openConfirmModal({
        title: "인사평가 메인페이지에서 접근하십시오.",
        okText: "확인",
        cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
        maskClosable: false, // 바깥 클릭 방지
        keyboard: false, // ESC 방지
        onOk: () => {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
        onCancel() {
          // 인사평가 메인 페이지로 이동
          common.openPage({ menuId: "MEVL2025002" });
        },
      });
    }
  }, [activeProps]);

  /**
   * 화면 크기 감지
   * 설명 : 화면 크기 변경시 상태 업데이트
   */
  React.useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  React.useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  /**
   * 프로젝트 정보 조회 함수
   * Redux에서 받은 props의 evlId와 empNo를 이용해 프로젝트 정보를 조회
   */
  const loadProjectInfo = () => {
    // activeProps에서 받은 pjNm을 바로 설정
    dispatch(
      Evl0107mAction.setProjectInfo({
        ...projectInfo,
        pjNm: activeProps?.pjNm || "",
      }),
    );

    const requestData = {
      pjMapId: activeProps?.evlId || "",
      empNo: activeProps?.empNo || "",
    };

    callAsyncSvc(requestData, {
      txCd: "TREVLR0014", // 프로젝트 정보 조회 트랜코드
      success: (responseData: any) => {
        let pjNm = activeProps?.pjNm || "";
        let userNm = "";
        let deptNm = "";

        // pmInfo 존재 여부 확인 후 접근
        if (responseData && responseData.pmInfo) {
          userNm = responseData.pmInfo.userNm || "";
          deptNm = responseData.pmInfo.deptNm || "";
        } else {
          message.error("pmInfo가 없습니다:", responseData);
        }

        // State에 프로젝트 정보 저장
        dispatch(
          Evl0107mAction.setProjectInfo({
            pjNm,
            userNm,
            deptNm,
          }),
        );

        // TREVLR0014 트랜잭션 완료 후 TREVLR0017 트랜잭션 호출
        loadEvaluationItems(requestData);
      },
      error: (err: any) => {
        message.error("프로젝트 정보 조회 실패:", err);
      },
    });
  };

  /**
   * 평가 데이터 조회 함수
   * TREVLR0017 트랜잭션 완료 후 호출
   */
  const loadEvaluationData = (
    requestData: any,
    evaluationDataParam: EvaluationItem[],
    isInitial: boolean = false,
  ) => {
    const payload = {
      pjMapId: activeProps?.evlId || "",
      evlCd: "03",
      ...requestData,
    };
    callAsyncSvc(payload, {
      txCd: "TREVLR0015", // 평가 데이터 조회 트랜코드
      success: (responseData: any) => {
        if (isInitial) {
          message.success("프로젝트 평가내용 조회완료");
        }

        // responseData.PjtEvlPm에서 pmEvlRmk를 가져와서 기타 평가 의견에 설정
        const pmEvlRmk = responseData?.PjtEvlPm?.[0]?.pmEvlRmk;
        if (pmEvlRmk != null) {
          form.setFieldsValue({ comments: pmEvlRmk });
        }

        // responseData.PjtEvlPm 배열의 각 항목에서 pmEvlScr을 hrEvlItmId를 key로 매핑
        if (
          responseData &&
          responseData.PjtEvlPm &&
          Array.isArray(responseData.PjtEvlPm)
        ) {
          const newPmScores: Record<string, string> = {};

          responseData.PjtEvlPm.forEach((evalItem: any) => {
            if (evalItem.pmEvlScr && evalItem.hrEvlItmId) {
              newPmScores[evalItem.hrEvlItmId] = String(evalItem.pmEvlScr);
            }
          });

          dispatch(Evl0107mAction.setPmScores(newPmScores));

          // pmSubYn 설정 및 제출 상태 확인
          const pmSubYnValue = responseData.PjtEvlPm[0]?.pmSubYn || "";
          dispatch(Evl0107mAction.setPmSubYn(pmSubYnValue));
          if (pmSubYnValue === "Y") {
            dispatch(Evl0107mAction.setIsSubmitted(true));
          } else {
            dispatch(Evl0107mAction.setIsSubmitted(false));
          }
        }

        // 마감일 이내 여부 설정 (서버에서 계산한 값 사용)
        const isWithinDeadlineValue =
          responseData?.isWithinDeadline === true ||
          responseData?.isWithinDeadline === "Y";
        setIsWithinDeadline(isWithinDeadlineValue);

        // 데이터 로드 완료
        if (isInitial) {
          setLoading(false);
        }
      },
      error: (err: any) => {
        message.error("평가 데이터 조회 실패:", err);
        if (isInitial) {
          setLoading(false);
        }
      },
    });
  };

  /**
   * 평가 항목 조회 함수
   * TREVLR0014 트랜잭션 완료 후 호출
   */
  const loadEvaluationItems = (requestData: any) => {
    const payload = { evlCd: "03", ...requestData };
    callAsyncSvc(payload, {
      txCd: "TREVLR0017", // 평가 항목 조회 트랜코드
      success: (responseData: any) => {
        // 기타가감 항목(dtlTypCd: '11')을 마지막으로 정렬
        const otherItems = responseData.evlItem.filter(
          (item: any) => item.dtlTypCd === "11",
        );
        const mainItems = responseData.evlItem.filter(
          (item: any) => item.dtlTypCd !== "11",
        );
        const sortedItems = [...mainItems, ...otherItems];

        // dtlTypCd별 baseAsgn 합계 계산
        const maxScores: Record<string, number> = {};
        sortedItems.forEach((item: any) => {
          if (!maxScores[item.dtlTypCd]) maxScores[item.dtlTypCd] = 0;
          maxScores[item.dtlTypCd] += item.baseAsgn;
        });
        // dispatch(Evl0107mAction.setMaxScores(maxScores)); // 제거

        // 그룹별로 묶고 소계 추가
        const groups: Record<string, any[]> = {};
        sortedItems.forEach((item) => {
          if (!groups[item.dtlTypCd]) groups[item.dtlTypCd] = [];
          groups[item.dtlTypCd].push(item);
        });

        // 그룹 순서 유지
        const groupOrder = Array.from(
          new Set(sortedItems.map((item) => item.dtlTypCd)),
        );
        const mainGroupOrder = groupOrder.filter((dtl) => dtl !== "11");
        const otherGroupOrder = groupOrder.filter((dtl) => dtl === "11");

        /* const seqMap: Record<string, string> = {
          "01": "①",
          "02": "②",
          "03": "③",
          "04": "④",
          "05": "⑤",
          "06": "⑥",
          "07": "⑦",
          "08": "⑧",
          "09": "⑨",
          "10": "⑩",
        }; */

        const data: EvaluationItem[] = [];

        // 메인 그룹들
        mainGroupOrder.forEach((dtlTypCd) => {
          data.push(
            ...groups[dtlTypCd].map((item: any, index: number) => ({
              key: item.hrEvlItmId,
              category: item.itmNm,
              no: item.itmSeq,
              item: /* seqMap[item.itmSeq] + " " + */ `${index + 1}. ${item.itmDesc}`,
              score: item.baseAsgn,
              pm: "",
              dtlTypCd: item.dtlTypCd,
            })),
          );

          // 소계 추가
          const subtotalScore = groups[dtlTypCd].reduce(
            (sum, item) => sum + item.baseAsgn,
            0,
          );
          data.push({
            key: `subtotal-${dtlTypCd}`,
            category: groups[dtlTypCd][0].itmNm,
            no: "",
            item: "소계",
            score: subtotalScore,
            pm: "",
            dtlTypCd: dtlTypCd,
          });
        });

        // 계 추가
        const totalScore = mainGroupOrder.reduce(
          (sum, dtlTypCd) =>
            sum + groups[dtlTypCd].reduce((s, item) => s + item.baseAsgn, 0),
          0,
        );
        data.push({
          key: "total",
          category: "",
          no: "",
          item: "계",
          score: totalScore,
          pm: "",
          dtlTypCd: "",
        });

        // 기타가감 그룹
        otherGroupOrder.forEach((dtlTypCd) => {
          data.push(
            ...groups[dtlTypCd].map((item: any, index: number) => ({
              key: item.hrEvlItmId,
              category: item.itmNm,
              no: item.itmSeq,
              item: /* seqMap[item.itmSeq] + " " + */ `${index + 1}. ${item.itmDesc}`,
              score: item.baseAsgn,
              pm: "",
              dtlTypCd: item.dtlTypCd,
            })),
          );

          // 기타가감에는 소계 추가하지 않음
        });

        // 총계 추가
        const grandTotalScore =
          totalScore +
          otherGroupOrder.reduce(
            (sum, dtlTypCd) =>
              sum + groups[dtlTypCd].reduce((s, item) => s + item.baseAsgn, 0),
            0,
          );
        data.push({
          key: "grand-total",
          category: "",
          no: "",
          item: "총계",
          score: grandTotalScore,
          pm: "",
          dtlTypCd: "",
        });

        dispatch(Evl0107mAction.setEvaluationData(data));
        loadEvaluationData(requestData, data, true);
      },
      error: (err: any) => {
        message.error("평가 항목 조회 실패:", err);
      },
    });
  };

  /**
   * 저장 버튼 클릭 핸들러
   * 현재 입력된 평가 데이터를 저장하는 기능
   */
  const handleSave = () => {
    openConfirmModal({
      title: "저장하시겠습니까?",
      okText: "저장",
      cancelText: "취소",
      onOk: () => {
        const comments = form.getFieldValue("comments") || "";

        // comments 500자 검증
        if (comments.length > 500) {
          openConfirmModal({
            title: "※ 500자를 초과하였습니다.",
            okText: "확인",
          });
          return;
        }

        const evlList = Object.keys(pmScores).map((hrEvlItmId) => ({
          hrEvlItmId,
          pmEvlScr: pmScores[hrEvlItmId],
        }));

        const requestData = {
          pjMapId: activeProps.evlId,
          evlCd: "03", // 03 : 프로젝트평가PM
          pmEvlRmk: comments,
          pmEvlTotScr: calculateGrandTotal(),
          evlList,
        };

        callAsyncSvc(requestData, {
          txCd: "TREVLS0016",
          success: (responseData: any) => {
            message.success("저장되었습니다.");
            // 저장 후 평가 데이터 재조회
            const requestData = {
              pjMapId: activeProps.evlId,
              empNo: activeProps?.empNo || "",
            };
            loadEvaluationData(requestData, evaluationData, false);
            // 스크롤은 useEffect에서 처리됨
          },
          error: (err: any) => {
            message.error("저장에 실패했습니다.");
            // 스크롤은 useEffect에서 처리됨
          },
        });
      },
    });
  };

  /**
   * comments 입력 변경 핸들러
   * 500자 초과 시 Modal.confirm으로 경고하고 500자로 제한
   */
  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > 500) {
      form.setFieldsValue({ comments: value.slice(0, 500) });
      openConfirmModal({
        title: "※ 500자를 초과하였습니다.",
        okText: "확인",
      });
    }
  };

  /**
   * 제출 버튼 클릭 핸들러
   * 평가 데이터를 최종 제출하는 기능
   * 평가하지 않은 항목이 있는지 체크 후 제출
   */
  const handleSubmit = () => {
    // 실제 평가 항목만 필터링 (소계, 계, 총계 제외, 기타가감 제외)
    const evaluationItems = evaluationData.filter(
      (item) =>
        item.no !== "" &&
        item.item !== "계" &&
        item.item !== "총계" &&
        item.dtlTypCd !== "11", // 기타가감은 필수검증 제외
    );

    // 평가하지 않은 항목 찾기
    const unevaluatedItems = evaluationItems.filter((item) => {
      const grade = item.key in pmScores ? pmScores[item.key] : item.pm;
      return !grade || grade === "";
    });

    // 평가하지 않은 항목이 있으면 경고
    if (unevaluatedItems.length > 0) {
      const countsByCategory = unevaluatedItems.reduce(
        (acc: Record<string, number>, item) => {
          const key = item.category || "미분류";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {},
      );
      const categoryOrder = evaluationItems.reduce((acc: string[], item) => {
        const key = item.category || "미분류";
        if (!acc.includes(key)) acc.push(key);
        return acc;
      }, []);
      const firstCategory = categoryOrder.find(
        (category) => (countsByCategory[category] || 0) > 0,
      );
      const fallbackCategory = Object.keys(countsByCategory)[0];
      const category = firstCategory || fallbackCategory || "미분류";
      const count = countsByCategory[category] || 0;
      message.error(`${category} - ${count}개의 항목이 평가되지 않았습니다.`);
      const firstMissing = unevaluatedItems[0];
      if (firstMissing) {
        scrollToRowSelector(`[data-eval-key="${firstMissing.key}"]`);
      } else if (evaluationTableRef.current) {
        scrollAndError(evaluationTableRef, "평가 항목을 확인해주세요.");
      }
      return;
    }

    const comments = (form.getFieldValue("comments") || "").trim();
    if (!comments) {
      Modal.warning({
        title: "기타 평가 의견 미입력",
        content: "기타 평가 의견을 입력해주세요.",
      });
      return;
    }

    // 기타가감 항목 선택 여부 확인
    const otherItem = evaluationData.find((item) => item.dtlTypCd === "11");
    const otherItemSelected =
      otherItem && otherItem.key in pmScores && pmScores[otherItem.key];

    // 기타가감 항목을 선택하지 않았을 경우
    if (!otherItemSelected) {
      openConfirmModal({
        title: "기타가감 항목 미선택 확인",
        content:
          "기타가감 항목을 선택하지 않았습니다. 그대로 제출하시겠습니까?",
        okText: "제출",
        cancelText: "취소",
        onOk: () => {
          submitEvaluation();
        },
      });
      return;
    }

    submitEvaluation();
  };

  /**
   * 평가 제출 실행 함수
   * handleSubmit에서 모든 검증을 완료한 후 호출
   */
  const submitEvaluation = () => {
    openConfirmModal({
      title: "제출하시겠습니까?",
      content: "제출 이후에는 수정이 불가합니다.",
      okText: "제출",
      cancelText: "취소",
      onOk: () => {
        const comments = form.getFieldValue("comments") || "";

        const evlList = Object.keys(pmScores).map((hrEvlItmId) => ({
          hrEvlItmId,
          pmEvlScr: pmScores[hrEvlItmId],
        }));

        const requestData = {
          pjMapId: activeProps.evlId,
          evlCd: "03", // 03 : 프로젝트평가PM
          pmEvlRmk: comments,
          pmEvlTotScr: calculateGrandTotal(),
          evlList,
        };

        callAsyncSvc(requestData, {
          txCd: "TREVLS0017",
          success: (responseData: any) => {
            const resultCode =
              responseData?.data?.resultCode || responseData?.resultCode;
            const resultMsg =
              responseData?.data?.resultMsg || responseData?.resultMsg;

            if (resultCode === "400") {
              openConfirmModal({
                title: "제출 실패",
                content: resultMsg || "제출에 실패했습니다.",
                okText: "확인",
                cancelButtonProps: { style: { display: "none" } },
                onOk: () => {
                  common.openPage({ menuId: "MEVL2025002" });
                },
              });
            } else {
              message.success("제출되었습니다.");
              setTimeout(() => {
                common.openPage({ menuId: "MEVL2025002" });
              }, 800);
            }
          },
          error: (err: any) => {
            message.error("제출에 실패했습니다.");
          },
        });
      },
    });
  };

  /**
   * 제출취소 버튼 클릭 핸들러
   */
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  /**
   * 실제 제출취소 로직 실행 (submitYn='N'으로 제출)
   */
  const performCancelSubmit = () => {
    const requestData = {
      pjMapId: activeProps.evlId,
      evlCd: "03",
      submitYn: "N",
    };

    callAsyncSvc(requestData, {
      txCd: "TREVLS0017",
      success: (responseData: any) => {
        const resultCode =
          responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg =
          responseData?.data?.resultMsg || responseData?.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "제출취소 실패",
            content: resultMsg || "제출취소에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          message.success("제출취소 되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (err: any) => {
        openConfirmModal({
          title: "오류",
          content: "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 등급별 점수 계산 함수
   * @param grade - 평가 등급 (A, B, C, D)
   * @param score - 배점
   * @returns 계산된 점수 (배점 × 등급 계수)
   *
   * 등급 계수:
   * - A: 1.0 (100%)
   * - B: 0.75 (75%)
   * - C: 0.5 (50%)
   * - D: 0.25 (25%)
   */
  const calculateScore = (grade: string, score: number): number => {
    const gradeMap: Record<string, number> = {
      A: 1.0,
      B: 0.75,
      C: 0.5,
      D: 0.25,
    };
    return score * (gradeMap[grade] || 0);
  };

  /**
   * 구분별 소계 계산 함수
   * @param category - 평가 구분 (일정, 인력, 품질, 비용)
   * @returns 소계 점수 (소수점 첫째자리까지, 0이면 빈 문자열)
   *
   * 로직:
   * 1. 해당 구분의 평가 항목들만 필터링 (소계 행 제외)
   * 2. 각 항목의 등급을 확인 (사용자가 선택한 값 우선, 없으면 초기값)
   * 3. 등급이 있는 경우에만 점수 계산하여 합산
   * 4. 총합이 0보다 크면 소수점 첫째자리까지 반환, 아니면 빈 문자열
   */
  const calculateSubtotal = (category: string): string => {
    // 해당 구분의 평가 항목만 필터링 (no가 빈 문자열인 소계 행은 제외)
    const categoryItems = evaluationData.filter(
      (item) => item.category === category && item.no !== "",
    );

    // 각 항목의 점수를 계산하여 합산
    const total = categoryItems.reduce((sum, item) => {
      // pmScores 객체에 명시적으로 있는지 확인 (사용자가 선택한 경우)
      // 없으면 초기값(item.pm) 사용
      const grade = item.key in pmScores ? pmScores[item.key] : item.pm;
      if (grade && grade !== "") {
        return sum + calculateScore(grade, item.score);
      }
      return sum;
    }, 0);

    // 총합이 0보다 크면 소수점 둘째자리 문자열로 반환, 아니면 빈 문자열
    return total > 0 ? total.toFixed(2) : "";
  };

  /**
   * 총계 계산 함수
   * @returns 총계 점수 (소수점 둘째자리까지, 0이면 빈 문자열)
   *
   * 로직:
   * 1. 4개 구분(일정, 인력, 품질, 비용)의 소계를 모두 합산
   * 2. 기타가감 항목의 점수를 추가
   * 3. 최종 총계 반환
   */
  const calculateGrandTotal = (): string => {
    const categories = ["일정", "인력", "품질", "비용"];
    let total = 0;

    // 각 구분의 소계를 합산
    categories.forEach((category) => {
      const subtotal = calculateSubtotal(category);
      if (subtotal) {
        total += parseFloat(subtotal);
      }
    });

    // 기타가감 항목 추가 (dtlTypCd: '11')
    const otherItem = evaluationData.find((item) => item.dtlTypCd === "11");
    if (otherItem) {
      const grade =
        otherItem.key in pmScores ? pmScores[otherItem.key] : otherItem.pm;
      if (grade && grade !== "") {
        total += calculateScore(grade, otherItem.score);
      }
    }

    return total > 0 ? total.toFixed(2) : "";
  };

  /**
   * dtlTypCd별 category 매핑
   */
  const categoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    evaluationData.forEach((item) => {
      if (item.dtlTypCd && !map[item.dtlTypCd]) {
        map[item.dtlTypCd] = item.category;
      }
    });
    return map;
  }, [evaluationData]);

  /**
   * 점수 요약 데이터
   */
  const summaryData = useMemo(() => {
    const mainDtlCds = groupOrder.filter((dtl) => dtl !== "11");
    const hasOther = evaluationData.some((item) => item.dtlTypCd === "11");
    let totalMaxScore = mainDtlCds.reduce(
      (sum, dtl) => sum + (maxScores[dtl] || 0),
      0,
    );
    if (hasOther) {
      totalMaxScore += maxScores["11"] || 0;
    }
    const data = [
      {
        key: "1",
        category: categoryMap[mainDtlCds[0]] || "",
        score: calculateSubtotal(categoryMap[mainDtlCds[0]] || "") || "0.00",
        maxScore: (maxScores[mainDtlCds[0]] || 0).toFixed(2),
      },
      {
        key: "2",
        category: categoryMap[mainDtlCds[1]] || "",
        score: calculateSubtotal(categoryMap[mainDtlCds[1]] || "") || "0.00",
        maxScore: (maxScores[mainDtlCds[1]] || 0).toFixed(2),
      },
      {
        key: "3",
        category: categoryMap[mainDtlCds[2]] || "",
        score: calculateSubtotal(categoryMap[mainDtlCds[2]] || "") || "0.00",
        maxScore: (maxScores[mainDtlCds[2]] || 0).toFixed(2),
      },
      {
        key: "4",
        category: categoryMap[mainDtlCds[3]] || "",
        score: calculateSubtotal(categoryMap[mainDtlCds[3]] || "") || "0.00",
        maxScore: (maxScores[mainDtlCds[3]] || 0).toFixed(2),
      },
    ];

    if (hasOther) {
      data.push({
        key: "5",
        category: categoryMap["11"] || "",
        score: (() => {
          const otherItem = evaluationData.find(
            (item) => item.dtlTypCd === "11",
          );
          if (otherItem) {
            const grade =
              otherItem.key in pmScores
                ? pmScores[otherItem.key]
                : otherItem.pm;
            if (grade && grade !== "") {
              return calculateScore(grade, otherItem.score).toFixed(2);
            }
          }
          return "0.00";
        })(),
        maxScore: (maxScores["11"] || 0).toFixed(2),
      });
    }
    data.push({
      key: hasOther ? "6" : "5",
      category: "총계",
      score: calculateGrandTotal() || "0.00",
      maxScore: totalMaxScore.toFixed(2),
    });
    return data;
  }, [categoryMap, maxScores, evaluationData, pmScores, groupOrder]);

  const hasActionButtons =
    isPmMatched && (pmSubYn !== "Y" || (pmSubYn === "Y" && isWithinDeadline));

  // 로딩 화면
  if (loading)
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          zIndex: 999, // 모달(z-index: 1000)보다 낮게 설정하여 background에만 표시
          background: "rgba(255,255,255,0.6)",
        }}
      >
        <Spin size="large" />
      </div>
    );

  return (
    <>
      <div
        style={{
          padding: isMobile ? "12px" : "20px",
          marginTop: "30px",
          backgroundColor: "#f5f5f5",
          width: screenWidth - 20 + "px",
          paddingBottom: hasActionButtons ? "50px" : "10px",
        }}
      >
        {/* 헤더 */}
        <div
          style={{
            textAlign: "center",
          }}
        >
          <Title level={3}>프로젝트 평가 (PM)</Title>
        </div>

        <Form form={form} layout="vertical">
          {/* 프로젝트 정보 */}
          <Card
            title={
              <span style={{ fontSize: "16px", fontWeight: 600 }}>
                프로젝트 정보
              </span>
            }
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "15px" }}
          >
            {/* PM, 소속 - 2컬럼 그리드 */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 1fr)",
                gap: "16px",
                marginBottom: "16px",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  PM
                </div>
                <div
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "#f5f5f5",
                    fontWeight: 500,
                    fontSize: "11px",
                    border: "1px solid #e8e8e8",
                    borderRadius: "6px",
                    textAlign: "center",
                  }}
                >
                  {projectInfo.userNm}
                </div>
              </div>
              <div>
                <div
                  style={{
                    fontSize: "12px",
                    marginBottom: "6px",
                    fontWeight: 500,
                  }}
                >
                  소속
                </div>
                <Tooltip title={projectInfo.deptNm}>
                  <div
                    style={{
                      padding: "6px 10px",
                      backgroundColor: "#f5f5f5",
                      fontWeight: 500,
                      fontSize: "11px",
                      border: "1px solid #e8e8e8",
                      borderRadius: "6px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      cursor: "pointer",
                      textAlign: "center",
                    }}
                  >
                    {projectInfo.deptNm}
                  </div>
                </Tooltip>
              </div>
            </div>

            {/* 프로젝트명 - 단일 컬럼 */}
            <div>
              <div
                style={{
                  fontSize: "12px",
                  marginBottom: "6px",
                  fontWeight: 500,
                }}
              >
                프로젝트명
              </div>
              <Tooltip
                title={projectInfo.pjNm}
                overlayInnerStyle={{ fontSize: "13px" }}
              >
                <div
                  style={{
                    padding: "6px 10px",
                    backgroundColor: "#f5f5f5",
                    fontWeight: 500,
                    fontSize: "11px",
                    border: "1px solid #e8e8e8",
                    borderRadius: "6px",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    cursor: "pointer",
                  }}
                >
                  {projectInfo.pjNm}
                </div>
              </Tooltip>
            </div>
          </Card>

          {/* 평가 항목 */}
          <Card
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px", color: "#333" }}>
                평가 항목
              </Text>
            </div>

            <div
              style={{
                padding: "12px",
                backgroundColor: "#f5f5f5",
                borderRadius: "8px",
                fontSize: "10px",
                color: "#666",
              }}
            >
              평가방법: A (우수), B (양호), C (보통), D (미흡)의 4단계로 평가
            </div>

            <div ref={evaluationTableRef}>
              <Space
                direction="vertical"
                size="small"
                style={{ width: "100%" }}
              >
                {groupOrder.map((dtlTypCd) => {
                  const groupItems = evaluationData.filter(
                    (item) =>
                      item.dtlTypCd === dtlTypCd &&
                      item.no !== "" &&
                      item.item !== "계" &&
                      item.item !== "총계" &&
                      item.item !== "소계",
                  );
                  if (groupItems.length === 0) return null;
                  const groupName = groupItems[0].category;
                  return (
                    <div key={dtlTypCd} style={{ marginBottom: "16px" }}>
                      <Divider
                        style={{
                          margin: "8px 0 8px 0",
                          fontSize: "14px",
                          fontWeight: 600,
                          borderColor: "#d9d9d9",
                        }}
                      >
                        {groupName}
                        {dtlTypCd !== "11" && (
                          <span style={{ color: "red" }}> * </span>
                        )}
                      </Divider>
                      <Table
                        dataSource={groupItems}
                        columns={[
                          {
                            title: (
                              <div style={{ textAlign: "center" }}>
                                평가항목
                              </div>
                            ),
                            dataIndex: "item",
                            render: (text) => (
                              <span style={{ fontSize: "12px" }}>{text}</span>
                            ),
                          },
                          {
                            title: "배점",
                            dataIndex: "score",
                            render: (text) => (
                              <span
                                style={{ fontSize: "12px", fontWeight: 500 }}
                              >
                                {text.toFixed(2)}
                              </span>
                            ),
                            width: 80,
                            align: "center" as const,
                          },
                          {
                            title: "PM",
                            dataIndex: "pm",
                            render: (text, record) => (
                              <Select
                                value={
                                  record.key in pmScores
                                    ? pmScores[record.key]
                                    : undefined
                                }
                                onChange={(value) => {
                                  const newPmScores = { ...pmScores };
                                  if (value === undefined) {
                                    delete newPmScores[record.key];
                                  } else {
                                    newPmScores[record.key] = value;
                                  }
                                  dispatch(
                                    Evl0107mAction.setPmScores(newPmScores),
                                  );
                                }}
                                style={{ width: "80px" }}
                                placeholder="선택"
                                allowClear
                                disabled={pmSubYn === "Y"}
                                options={[
                                  { value: "A", label: "A" },
                                  { value: "B", label: "B" },
                                  { value: "C", label: "C" },
                                  { value: "D", label: "D" },
                                ]}
                              />
                            ),
                            width: 100,
                            align: "center" as const,
                          },
                        ]}
                        pagination={false}
                        size="small"
                        bordered
                        onRow={(record) =>
                          ({
                            "data-eval-key": record.key,
                          }) as React.HTMLAttributes<HTMLElement>
                        }
                      />
                    </div>
                  );
                })}
              </Space>
            </div>
          </Card>

          {/* 총점 표시 */}
          <Card
            style={{
              marginBottom: "12px",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            {/* 점수 요약 */}
            <div style={{}}>
              <div style={{ marginBottom: "16px" }}>
                <Text strong style={{ fontSize: "16px", color: "#333" }}>
                  평가 결과
                </Text>
              </div>

              <Table
                dataSource={summaryData}
                columns={[
                  {
                    title: "구분",
                    dataIndex: "category",
                    key: "category",
                    align: "center" as const,
                    width: 100,
                  },
                  {
                    title: "배점",
                    dataIndex: "maxScore",
                    key: "maxScore",
                    align: "center" as const,
                    width: 80,
                  },
                  {
                    title: "점수",
                    dataIndex: "score",
                    key: "score",
                    align: "center" as const,
                    width: 80,
                    render: (text, record) => {
                      if (record.category === "총계") {
                        return (
                          <span style={{ color: "red", fontWeight: "bold" }}>
                            {text}
                          </span>
                        );
                      }
                      return text;
                    },
                  },
                ]}
                pagination={false}
                bordered
                size="small"
              />
            </div>
            {/* <div style={{ textAlign: 'center' }}>
            <Text strong style={{ fontSize: '16px' }}>
              총점 : {calculateGrandTotal() || '0.0'} / {summaryData[summaryData.length - 1].maxScore}
            </Text>
          </div> */}
          </Card>

          {/* 기타 평가 의견 */}
          <Card
            style={{
              marginBottom: "12px",
              borderRadius: "12px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            bodyStyle={{ padding: "16px" }}
          >
            <div style={{ marginBottom: "16px" }}>
              <Text strong style={{ fontSize: "16px", color: "#333" }}>
                기타 평가 의견
              </Text>
            </div>

            <Form.Item name="comments">
              {/* <div ref={commentsRef}> */}
              <TextArea
                rows={6}
                placeholder="평가 의견을 입력하세요"
                disabled={pmSubYn === "Y"}
                onChange={handleCommentsChange}
                style={{ borderRadius: "8px" }}
                maxLength={500}
                showCount
              />
              {/* </div> */}
            </Form.Item>
          </Card>
        </Form>
      </div>

      {/* 저장 및 제출 버튼 - 바깥쪽 고정 배치 */}
      {hasActionButtons && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "12px",
            backgroundColor: "#fff",
            borderTop: "1px solid #f0f0f0",
            display: "flex",
            gap: "8px",
            zIndex: 1000,
          }}
        >
          {pmSubYn !== "Y" && (
            <>
              <Button
                size="large"
                icon={<SaveOutlined />}
                onClick={handleSave}
                disabled={isSubmitted}
                style={{
                  flex: 1,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                저장
              </Button>
              <Button
                type="primary"
                size="large"
                icon={<SendOutlined />}
                onClick={handleSubmit}
                disabled={isSubmitted}
                style={{
                  flex: 1,
                  height: "48px",
                  fontSize: "16px",
                  fontWeight: 600,
                }}
              >
                제출
              </Button>
            </>
          )}

          {pmSubYn === "Y" && isWithinDeadline && (
            <Button
              danger
              size="large"
              onClick={handleCancelSubmit}
              style={{
                flex: 1,
                height: "48px",
                fontSize: "16px",
                fontWeight: 600,
              }}
            >
              제출취소
            </Button>
          )}
        </div>
      )}
    </>
  );
}
